/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import React, { useState, useEffect } from 'react';

//@ts-ignore
import { Primary } from '@churchofjesuschrist/eden-buttons';
import { ILdsStoreProps } from './lds-store-locator';
import { IChurchGlobalStoreLocatorInformation, ICountryLanguageEntity } from '../../actions/DataServiceEntities.g';

import { IStoreSelectorLocationLineItemResources } from './components/timeHelperData';
import { IHoursDisplayInfo, buildStoreHours } from './components/timeHelper';
import { StoreLocationSelector } from './components/StoreLocationSelector';
import { StoreLocatorTable } from './components/StoreLocatorTable';

import { getStoreHoursAsync } from '../../actions/DataActionExtension.g';

const LdsStoreLocatorView: React.FC<ILdsStoreProps> = props => {
    const StatesDefaultList = [
        { name: 'Alabama', abbreviation: 'AL' },
        { name: 'Alaska', abbreviation: 'AK' },
        { name: 'Arizona', abbreviation: 'AZ' },
        { name: 'Arkansas', abbreviation: 'AR' },
        { name: 'California', abbreviation: 'CA' },
        { name: 'Colorado', abbreviation: 'CO' },
        { name: 'Connecticut', abbreviation: 'CT' },
        { name: 'Delaware', abbreviation: 'DE' },
        { name: 'Florida', abbreviation: 'FL' },
        { name: 'Georgia', abbreviation: 'GA' },
        { name: 'Hawaii', abbreviation: 'HI' },
        { name: 'Idaho', abbreviation: 'ID' },
        { name: 'Illinois', abbreviation: 'IL' },
        { name: 'Indiana', abbreviation: 'IN' },
        { name: 'Iowa', abbreviation: 'IA' },
        { name: 'Kansas', abbreviation: 'KS' },
        { name: 'Kentucky', abbreviation: 'KY' },
        { name: 'Louisiana', abbreviation: 'LA' },
        { name: 'Maine', abbreviation: 'ME' },
        { name: 'Maryland', abbreviation: 'MD' },
        { name: 'Massachusetts', abbreviation: 'MA' },
        { name: 'Michigan', abbreviation: 'MI' },
        { name: 'Minnesota', abbreviation: 'MN' },
        { name: 'Mississippi', abbreviation: 'MS' },
        { name: 'Missouri', abbreviation: 'MO' },
        { name: 'Montana', abbreviation: 'MT' },
        { name: 'Nebraska', abbreviation: 'NE' },
        { name: 'Nevada', abbreviation: 'NV' },
        { name: 'New Hampshire', abbreviation: 'NH' },
        { name: 'New Jersey', abbreviation: 'NJ' },
        { name: 'New Mexico', abbreviation: 'NM' },
        { name: 'New York', abbreviation: 'NY' },
        { name: 'North Carolina', abbreviation: 'NC' },
        { name: 'North Dakota', abbreviation: 'ND' },
        { name: 'Ohio', abbreviation: 'OH' },
        { name: 'Oklahoma', abbreviation: 'OK' },
        { name: 'Oregon', abbreviation: 'OR' },
        { name: 'Pennsylvania', abbreviation: 'PA' },
        { name: 'Rhode Island', abbreviation: 'RI' },
        { name: 'South Carolina', abbreviation: 'SC' },
        { name: 'South Dakota', abbreviation: 'SD' },
        { name: 'Tennessee', abbreviation: 'TN' },
        { name: 'Texas', abbreviation: 'TX' },
        { name: 'Utah', abbreviation: 'UT' },
        { name: 'Vermont', abbreviation: 'VT' },
        { name: 'Virginia', abbreviation: 'VA' },
        { name: 'Washington', abbreviation: 'WA' },
        { name: 'West Virginia', abbreviation: 'WV' },
        { name: 'Wisconsin', abbreviation: 'WI' },
        { name: 'Wyoming', abbreviation: 'WY' }
    ];
    const [stateList, setStateList] = useState(StatesDefaultList);

    const [storeRegion, setStoreRegion] = useState<any>('');
    const [regionList, setRegionList] = useState<ICountryLanguageEntity[]>();
    const [state, setState] = useState('');
    const [displayState, setDisplayStates] = useState(false);
    const [regionStores, setRegionStores] = useState<IChurchGlobalStoreLocatorInformation[]>([]);
    const [storeHours, setStoreHours] = useState<IHoursDisplayInfo[][]>();
    const [filteredRegions, setFilteredRegions] = useState<any>();
    const [storesFound, setStoresFound] = useState<boolean>(true);

    const RegionList = props.data.countries?.result;
    let formattedStoreHours: any;
    const StoreHoursContext: IStoreSelectorLocationLineItemResources = {
        contactInfoHeader: props.resources.contactText,
        storeHoursHeader: props.resources.timeText,
        availabilityInfoHeader: props.resources.availabilityText,

        closedText: props.resources.storeHoursClosedText,
        outOfStock: props.resources.outOfStockText,
        inStock: props.resources.inStockText,

        currentLocation: props.resources.selectedStoreLocationText,
        selectLocation: props.resources.selectStoreText,
        selectLocationAriaLabelFormat: props.resources.selectStoreAriaFormatText,
        setAsPreferredStoreText: props.resources.setAsPreferredStoreText,
        setAsPreferredStoreTextAriaLabel: props.resources.setAsPreferredStoreTextAriaLabel,
        preferredStoreText: props.resources.preferredStoreText,
        preferredStoreAriaLabel: props.resources.preferredStoreAriaLabel,

        days: {
            monday: props.resources.storeHoursMondayText,
            tuesday: props.resources.storeHoursTuesdayText,
            wednesday: props.resources.storeHoursWednesdayText,
            thursday: props.resources.storeHoursThursdayText,
            friday: props.resources.storeHoursFridayText,
            saturday: props.resources.storeHoursSaturdayText,
            sunday: props.resources.storeHoursSundayText,
            mondayFull: props.resources.storeHoursMondayFullText,
            tuesdayFull: props.resources.storeHoursTuesdayFullText,
            wednesdayFull: props.resources.storeHoursWednesdayFullText,
            thursdayFull: props.resources.storeHoursThursdayFullText,
            fridayFull: props.resources.storeHoursFridayFullText,
            saturdayFull: props.resources.storeHoursSaturdayFullText,
            sundayFull: props.resources.storeHoursSundayFullText
        }
    };
    let StateSelectOptions: any = StatesDefaultList;
    const cart = props.data.cart?.result;
    const filterFunction = async () => {
        const storesWithHours = await getStoreHoursAsync({ callerContext: props.context.actionContext }, '0');
        if (storesWithHours && regionList) {
            const filteredRegions = regionList.filter(el => {
                return storesWithHours.some(f => {
                    return f.COUNTRYREGIONID === el.countryRegionId;
                });
            });
            setFilteredRegions(filteredRegions);
        }
    };
    const getAvailableState = async () => {
        await handleChange('USA');
        setDisplayStates(true);
        StateSelectOptions = StatesDefaultList.filter(({ abbreviation: id1 }) => regionStores.some(({ STATE: id2 }) => id2 === id1));
        setStateList(StateSelectOptions);
    };

    useEffect(() => {
        if (cart && !storeRegion && cart.cart.ExtensionProperties) {
            const userCountry = cart.cart.ExtensionProperties?.find((x: any) => x.Key === 'Country')?.Value?.StringValue;
            setStoreRegion(userCountry);
            void handleChange(userCountry!);
        }
        if (storeRegion === 'USA' && !displayState) {
            void getAvailableState();
        } else if (storeRegion !== 'USA') {
            setDisplayStates(false);
        }

        if (RegionList && !filteredRegions) {
            const sortedRegions = RegionList.sort((a, b) =>
                a.CountryName! > b.CountryName! ? 1 : b.CountryName! > a.CountryName! ? -1 : 0
            );
            setRegionList(sortedRegions);
            void filterFunction();
        }

        if (regionStores.length >= 1) {
            formattedStoreHours = regionStores.map(store => {
                return buildStoreHours(store, StoreHoursContext);
            });
            setStoreHours(formattedStoreHours);
        }
        if (!storesFound && filteredRegions && filteredRegions[0]) {
            void handleChange(filteredRegions[0].countryRegionId);
        }
    }, [storeRegion, regionStores, RegionList, storesFound, filteredRegions]);

    const handleChange = async (value: string) => {
        const getRegionStores = await getStoreHoursAsync({ callerContext: props.context.actionContext }, value);
        if (getRegionStores[0] !== undefined) {
            const sortedRegionStores = getRegionStores.sort((a: any, b: any) => {
                const compareStrings = (str1: any, str2: any) => {
                    const trimmedStr1 = str1.startsWith('DS ') ? str1.slice(3) : str1.startsWith('Deseret Book ') ? str1.slice(13) : str1;
                    const trimmedStr2 = str2.startsWith('DS ') ? str2.slice(3) : str2.startsWith('Deseret Book ') ? str2.slice(13) : str2;

                    for (let i = 0; i < Math.min(trimmedStr1.length, trimmedStr2.length); i++) {
                        const charCode1 = trimmedStr1.charCodeAt(i);
                        const charCode2 = trimmedStr2.charCodeAt(i);

                        if (charCode1 < charCode2) {
                            return -1;
                        } else if (charCode1 > charCode2) {
                            return 1;
                        }
                    }

                    if (trimmedStr1.length < trimmedStr2.length) {
                        return -1;
                    } else if (trimmedStr1.length > trimmedStr2.length) {
                        return 1;
                    }

                    return 0;
                };

                return compareStrings(a.NAME, b.NAME);
            });

            // formattedStoreHours = getRegionStores.map(store => {
            //     return buildStoreHours(store, StoreHoursContext);
            // });
            formattedStoreHours = sortedRegionStores.map(store => {
                return buildStoreHours(store, StoreHoursContext);
            });

            setStoreHours(formattedStoreHours);

            // setRegionStores(getRegionStores);
            setRegionStores(sortedRegionStores);
            setStoreRegion(value);
            setStoresFound(true);
        } else {
            setStoresFound(false);
        }
    };

    const handleClick = async (state: string) => {
        const getStores = await getStoreHoursAsync({ callerContext: props.context.actionContext }, storeRegion);
        setState(state);

        const filteredStores = state
            ? getStores.filter(store => {
                  return store.STATE === state;
              })
            : null;
        let sortedRegionStores;
        if (filteredStores) {
            sortedRegionStores = filteredStores.sort((a: any, b: any) => {
                const compareStrings = (str1: any, str2: any) => {
                    const trimmedStr1 = str1.startsWith('DS ') ? str1.slice(3) : str1.startsWith('Deseret Book ') ? str1.slice(13) : str1;
                    const trimmedStr2 = str2.startsWith('DS ') ? str2.slice(3) : str2.startsWith('Deseret Book ') ? str2.slice(13) : str2;

                    for (let i = 0; i < Math.min(trimmedStr1.length, trimmedStr2.length); i++) {
                        const charCode1 = trimmedStr1.charCodeAt(i);
                        const charCode2 = trimmedStr2.charCodeAt(i);

                        if (charCode1 < charCode2) {
                            return -1;
                        } else if (charCode1 > charCode2) {
                            return 1;
                        }
                    }

                    if (trimmedStr1.length < trimmedStr2.length) {
                        return -1;
                    } else if (trimmedStr1.length > trimmedStr2.length) {
                        return 1;
                    }

                    return 0;
                };

                // Compare the strings using the custom comparison function
                return compareStrings(a.NAME, b.NAME);
            });
        }

        // if (filteredStores) {
        //     formattedStoreHours = filteredStores.map(store => {
        //         return buildStoreHours(store, StoreHoursContext);
        //     });
        //     setStoreHours(formattedStoreHours);
        // }
        if (sortedRegionStores) {
            formattedStoreHours = sortedRegionStores.map(store => {
                return buildStoreHours(store, StoreHoursContext);
            });
            setStoreHours(formattedStoreHours);
        }

        // setRegionStores(filteredStores ? filteredStores : getStores);
        setRegionStores(sortedRegionStores ? sortedRegionStores : getStores);
    };

    const { storeLocatorHeader, storeLocatorMessage } = props.resources;

    return (
        <div className='StoreLocator'>
            <div className='StoreLocatorWrapper'>
                <>
                    <h1 className='Header'>{storeLocatorHeader}</h1>
                    <p className='StoreLocatorBody'>{storeLocatorMessage}</p>
                </>
                <StoreLocationSelector
                    storeRegion={storeRegion}
                    handleChange={handleChange}
                    state={state}
                    handleClick={handleClick}
                    regionList={filteredRegions ? filteredRegions : regionList}
                    displayState={displayState}
                    stateList={stateList}
                    resources={props.resources}
                    regionStores={regionStores}
                />
                <StoreLocatorTable regionStores={regionStores} storeHours={storeHours} resources={props.resources} />
            </div>
        </div>
    );
};

export default LdsStoreLocatorView;
